'use client';

import { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { usePublicUi } from '@/lib/publicUi';
import styles from './ExternalLinkConfirmDialog.module.css';

const URL_DISPLAY_MAX = 180;

export function externalLinkDisplayHost(href: string): string {
  const trimmed = href.trim();
  try {
    const lower = trimmed.toLowerCase();
    if (lower.startsWith('http://') || lower.startsWith('https://')) {
      return new URL(trimmed).hostname || trimmed;
    }
  } catch {
    /* fall through */
  }
  return trimmed;
}

function truncateUrl(href: string): string {
  if (href.length <= URL_DISPLAY_MAX) return href;
  return `${href.slice(0, URL_DISPLAY_MAX - 1)}…`;
}

export function ExternalLinkConfirmDialog({
  href,
  openInNewTab,
  onCancel,
  onContinue,
}: {
  href: string;
  openInNewTab: boolean;
  onCancel: () => void;
  onContinue: () => void;
}) {
  const { extra } = usePublicUi();
  const host = externalLinkDisplayHost(href);
  const body = extra.externalLinkConfirmBody.replace('{host}', host);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    },
    [onCancel],
  );

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onKeyDown]);

  // Portal to document.body so ViewingCanvas pointer capture / touch-action /
  // user-select on the viewport cannot swallow dialog clicks (ShareReportDialog
  // is also mounted outside the canvas tree).
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={styles.backdrop}
      role="presentation"
      data-external-link-dialog="1"
      onClick={onCancel}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div
        className={styles.card}
        role="dialog"
        aria-labelledby="external-link-title"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="external-link-title">{extra.externalLinkConfirmTitle}</h2>
        <p className={styles.body}>{body}</p>
        <span className={styles.host}>{host}</span>
        <span className={styles.url} title={href}>
          {truncateUrl(href)}
        </span>
        <div className={styles.actions}>
          <button type="button" onClick={onCancel}>
            {extra.externalLinkConfirmCancel}
          </button>
          <button
            type="button"
            className={styles.primary}
            onClick={onContinue}
            data-open-in-new-tab={openInNewTab ? '1' : '0'}
          >
            {extra.externalLinkConfirmContinue}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
